import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { IconButton } from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
} from "@mui/icons-material";
import { toastMessage } from "../../utils/toastMessage";
import Loader from "../../comman/Loader";
import { segment } from "../../routes/route";
import { Input, Select, TextArea } from "../../components/Form";
import useButtonLoader from "../../hooks/UseButtonLoader";
import { allowAlphaNumericWithSpace } from "../../utils/regex";
import {
  getCommonEventsApi,
  getCommonTraitsApi,
  getSegmentApi,
  createSegmentApi,
  updateSegmentApi,
} from "../../services/segmentManagement";
import { getZodiacSignOptions } from "../../services/zodiacMessageManagement";

const SegmentBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});
  const [commonEvents, setCommonEvents] = useState([]);
  const [commonTraits, setCommonTraits] = useState([]);
  
  // Trait option lists
  const zodiacOptions = getZodiacSignOptions();
  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Transgender', label: 'Transgender' }
  ];
  const problemOptions = [
    { value: 'depression', label: 'Depression' },
    { value: 'career', label: 'Career' },
    { value: 'relationship', label: 'Relationship' },
    { value: 'marriage', label: 'Marriage' },
    { value: 'finance', label: 'Finance' },
    { value: 'exam', label: 'Exam' },
    { value: 'health', label: 'Health' },
    { value: 'family', label: 'Family' }
  ];
  
  const [segmentData, setSegmentData] = useState({
    name: "",
    description: "",
    interakt_tag: "",
    status: "draft",
    logic: {
      op: "AND",
      children: [],
    },
  });
  const [buttonref, buttonLoader] = useButtonLoader("Save Segment");

  useEffect(() => {
    fetchCommonEvents();
    fetchCommonTraits();
    if (id) {
      fetchSegment();
    }
  }, [id]);

  const fetchCommonEvents = async () => {
    try {
      const events = await getCommonEventsApi();
      setCommonEvents(events);
      if (events.length > 0) {
        console.log("Common events loaded:", events.length);
      }
    } catch (error) {
      console.error("Error fetching common events:", error);
      setCommonEvents([]);
    }
  };

  const fetchCommonTraits = async () => {
    try {
      const traits = await getCommonTraitsApi();
      setCommonTraits(traits);
      if (traits.length > 0) {
        console.log("Common traits loaded:", traits.length);
      }
    } catch (error) {
      console.error("Error fetching common traits:", error);
      setCommonTraits([]);
    }
  };

  const fetchSegment = async () => {
    try {
      setLoading(true);
      const segmentData = await getSegmentApi(id);
      setSegmentData({
        name: segmentData.name,
        description: segmentData.description || "",
        interakt_tag: segmentData.interakt_tag,
        status: segmentData.status,
        logic: segmentData.logic || { op: "AND", children: [] },
      });
    } catch (error) {
      toastMessage(error.response?.data?.message || error.message || "Error fetching segment", "error");
      navigate(segment.SEGMENT_LIST);
    } finally {
      setLoading(false);
    }
  };

  const validation = () => {
    let status = true;
    let error = {};

    if (!segmentData.name) {
      error["name"] = "Segment name is required *";
      status = false;
    }
    if (!segmentData.interakt_tag) {
      error["interakt_tag"] = "Interakt tag is required *";
      status = false;
    }
    if (!segmentData.logic.children || segmentData.logic.children.length === 0) {
      error["logic"] = "Please add at least one condition *";
      status = false;
    }
    setError(error);
    return status;
  };

  const handleSave = async () => {
    if (validation()) {
      try {
        buttonLoader(true);
        if (id) {
          await updateSegmentApi(id, segmentData);
          toastMessage("Segment updated successfully", "success");
        } else {
          await createSegmentApi(segmentData);
          toastMessage("Segment created successfully", "success");
        }
        navigate(segment.SEGMENT_LIST);
      } catch (error) {
        toastMessage(error.response?.data?.message || error.message || "Error saving segment", "error");
      } finally {
        buttonLoader(false);
      }
    }
  };

  const addCondition = (parentPath = []) => {
    const newCondition = {
      type: "condition",
      condition: {
        type: "event",
        event_name: "",
        operator: "done",
        time_range: { type: "last_n_days", n: 30 },
      },
    };

    const newLogic = { ...segmentData.logic };
    if (parentPath.length === 0) {
      newLogic.children = [...(newLogic.children || []), newCondition];
    } else {
      let current = newLogic;
      for (let i = 0; i < parentPath.length - 1; i++) {
        current = current.children[parentPath[i]];
      }
      if (!current.children) current.children = [];
      current.children.push(newCondition);
    }
    setSegmentData({ ...segmentData, logic: newLogic });
  };

  // Group functionality commented out - not needed currently
  // const addGroup = (parentPath = []) => {
  //   const newGroup = {
  //     type: "group",
  //     op: "AND",
  //     children: [],
  //   };

  //   const newLogic = { ...segmentData.logic };
  //   if (parentPath.length === 0) {
  //     newLogic.children = [...(newLogic.children || []), newGroup];
  //   } else {
  //     let current = newLogic;
  //     for (let i = 0; i < parentPath.length - 1; i++) {
  //       current = current.children[parentPath[i]];
  //     }
  //     if (!current.children) current.children = [];
  //     current.children.push(newGroup);
  //   }
  //   setSegmentData({ ...segmentData, logic: newLogic });
  // };

  const removeNode = (path) => {
    const newLogic = { ...segmentData.logic };
    if (path.length === 1) {
      newLogic.children.splice(path[0], 1);
    } else {
      let current = newLogic;
      for (let i = 0; i < path.length - 1; i++) {
        current = current.children[path[i]];
      }
      current.children.splice(path[path.length - 1], 1);
    }
    setSegmentData({ ...segmentData, logic: newLogic });
  };

  const updateNode = (path, updates) => {
    const newLogic = { ...segmentData.logic };
    let current = newLogic;
    for (let i = 0; i < path.length - 1; i++) {
      current = current.children[path[i]];
    }
    const target = current.children[path[path.length - 1]];
    Object.assign(target, updates);
    setSegmentData({ ...segmentData, logic: newLogic });
  };

  const updateCondition = (path, conditionUpdates) => {
    const newLogic = { ...segmentData.logic };
    let current = newLogic;
    for (let i = 0; i < path.length - 1; i++) {
      current = current.children[path[i]];
    }
    const target = current.children[path[path.length - 1]];
    target.condition = { ...target.condition, ...conditionUpdates };
    setSegmentData({ ...segmentData, logic: newLogic });
  };

  const renderCondition = (node, path) => {
    const condition = node.condition || {};
    const conditionNumber = path.length > 0 ? path[path.length - 1] + 1 : 1;
    return (
      <div
        style={{
          marginBottom: "30px",
          padding: "24px",
          backgroundColor: "#fff",
          borderRadius: "10px",
          border: "1px solid #e1dfdf",
          borderLeft: "5px solid #FF8989",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          position: "relative",
          transition: "all 0.2s ease"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            paddingBottom: "18px",
            borderBottom: "2px solid #f0f0f0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor: "#FF8989",
                color: "#fff",
                fontSize: "14px",
                fontWeight: "700",
                boxShadow: "0 2px 4px rgba(255, 137, 137, 0.3)"
              }}
            >
              {conditionNumber}
            </span>
            <h5 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "#242424" }}>
              Condition {conditionNumber}
            </h5>
          </div>
          <IconButton
            size="small"
            onClick={() => removeNode(path)}
            style={{ 
              color: "#d32f2f", 
              padding: "8px",
              backgroundColor: "#ffebee",
              transition: "all 0.2s ease"
            }}
            title="Delete Condition"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#ffcdd2";
              e.currentTarget.style.transform = "scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#ffebee";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px"
        }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <Select
              label="Condition Type"
              value={condition.type || "event"}
              onChange={(e) => updateCondition(path, { type: e.target.value })}
              className="form-control"
            >
              <option value="event">Event</option>
              <option value="trait">Trait</option>
            </Select>
          </div>
          {condition.type === "event" ? (
            <>
              <div>
                <Select
                  label="Event Name"
                  value={condition.event_name || ""}
                  onChange={(e) => updateCondition(path, { event_name: e.target.value })}
                  className="form-control"
                >
                  <option value="">Select Event</option>
                  {commonEvents && commonEvents.length > 0 ? (
                    commonEvents.map((event) => (
                      <option key={event} value={event}>
                        {event}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>Loading events...</option>
                  )}
                </Select>
                {commonEvents.length === 0 && (
                  <small style={{ color: "#999", fontSize: "12px", display: "block", marginTop: "4px" }}>
                    Loading events...
                  </small>
                )}
              </div>
              <div>
                <Select
                  label="Operator"
                  value={condition.operator || "done"}
                  onChange={(e) => updateCondition(path, { operator: e.target.value })}
                  className="form-control"
                >
                  <option value="done">Done</option>
                  <option value="not_done">Not Done</option>
                  <option value="count_gte">Count &gt;=</option>
                  <option value="count_lt">Count &lt;</option>
                  <option value="count_eq">Count =</option>
                </Select>
              </div>
              {(condition.operator === "count_gte" ||
                condition.operator === "count_lt" ||
                condition.operator === "count_eq") && (
                <div>
                  <Input
                    type="number"
                    label="Count"
                    value={condition.count || 0}
                    onChange={(e) => updateCondition(path, { count: parseInt(e.target.value) || 0 })}
                    className="form-control"
                    min="0"
                  />
                </div>
              )}
              <div>
                <Select
                  label="Time Range"
                  value={condition.time_range?.type || "last_n_days"}
                  onChange={(e) => {
                    const timeRange = { type: e.target.value };
                    if (
                      e.target.value === "last_n_days" ||
                      e.target.value === "last_n_hours" ||
                      e.target.value === "last_n_weeks" ||
                      e.target.value === "last_n_months"
                    ) {
                      timeRange.n = condition.time_range?.n || 30;
                    }
                    updateCondition(path, { time_range: timeRange });
                  }}
                  className="form-control"
                >
                  <option value="last_n_days">Last N Days</option>
                  <option value="last_n_hours">Last N Hours</option>
                  <option value="last_n_weeks">Last N Weeks</option>
                  <option value="last_n_months">Last N Months</option>
                  <option value="lifetime">Lifetime</option>
                </Select>
              </div>
              {(condition.time_range?.type === "last_n_days" ||
                condition.time_range?.type === "last_n_hours" ||
                condition.time_range?.type === "last_n_weeks" ||
                condition.time_range?.type === "last_n_months") && (
                <div>
                  <Input
                    type="number"
                    label="N Value"
                    value={condition.time_range?.n || 30}
                    onChange={(e) =>
                      updateCondition(path, {
                        time_range: { ...condition.time_range, n: parseInt(e.target.value) || 30 },
                      })
                    }
                    className="form-control"
                    min="1"
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <Select
                  label="Trait Key"
                  value={condition.key || ""}
                  onChange={(e) => updateCondition(path, { key: e.target.value })}
                  className="form-control"
                >
                  <option value="">Select Trait</option>
                  {commonTraits && commonTraits.length > 0 ? (
                    commonTraits.map((trait) => (
                      <option key={trait} value={trait}>
                        {trait}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>Loading traits...</option>
                  )}
                </Select>
                {commonTraits.length === 0 && (
                  <small style={{ color: "#999", fontSize: "12px", display: "block", marginTop: "4px" }}>
                    Loading traits...
                  </small>
                )}
              </div>
              <div>
                <Select
                  label="Operator"
                  value={condition.operator || "is"}
                  onChange={(e) => updateCondition(path, { operator: e.target.value })}
                  className="form-control"
                >
                  <option value="is">Is</option>
                  <option value="isnt">Is Not</option>
                  <option value="contains">Contains</option>
                  <option value="exists">Exists</option>
                  <option value="not_exists">Not Exists</option>
                  <option value="in">In</option>
                  <option value="gt">Greater Than</option>
                  <option value="gte">Greater Than or Equal</option>
                  <option value="lt">Less Than</option>
                  <option value="lte">Less Than or Equal</option>
                </Select>
              </div>
              {condition.operator !== "exists" && condition.operator !== "not_exists" && (
                <div>
                  {/* Show dropdown for specific trait keys */}
                  {(condition.key === "zodiac" || condition.key === "zodiac_sign") ? (
                    <Select
                      label="Zodiac Sign"
                      value={condition.value || ""}
                      onChange={(e) => updateCondition(path, { value: e.target.value })}
                      className="form-control"
                    >
                      <option value="">Select Zodiac Sign</option>
                      {zodiacOptions.map((zodiac) => (
                        <option key={zodiac.value} value={zodiac.value}>
                          {zodiac.label}
                        </option>
                      ))}
                    </Select>
                  ) : condition.key === "gender" ? (
                    <Select
                      label="Gender"
                      value={condition.value || ""}
                      onChange={(e) => updateCondition(path, { value: e.target.value })}
                      className="form-control"
                    >
                      <option value="">Select Gender</option>
                      {genderOptions.map((gender) => (
                        <option key={gender.value} value={gender.value}>
                          {gender.label}
                        </option>
                      ))}
                    </Select>
                  ) : (condition.key === "problem" || condition.key === "problems") ? (
                    <Select
                      label="Problem"
                      value={condition.value || ""}
                      onChange={(e) => updateCondition(path, { value: e.target.value })}
                      className="form-control"
                    >
                      <option value="">Select Problem</option>
                      {problemOptions.map((problem) => (
                        <option key={problem.value} value={problem.value}>
                          {problem.label}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      type="text"
                      label="Value"
                      value={condition.value || ""}
                      onChange={(e) => updateCondition(path, { value: e.target.value })}
                      placeholder="Enter value"
                      className="form-control"
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Group rendering commented out - not needed currently
  // Simplified version that only handles root level conditions
  const renderGroup = (node, path) => {
    const isRoot = path.length === 0;
    // Only render if this is root - nested groups are disabled
    if (!isRoot) return null;
    return (
      <div
        className="FilterBox FilterArea"
        style={{
          marginBottom: "30px",
          padding: "25px",
          backgroundColor: isRoot ? "#fff" : "#f8f9fa",
          borderRadius: "8px",
          border: isRoot ? "1px solid #e1dfdf" : "2px solid #FF8989",
          borderLeft: isRoot ? "1px solid #e1dfdf" : "4px solid #FF8989",
          boxShadow: isRoot ? "0 2px 4px rgba(0,0,0,0.05)" : "0 2px 8px rgba(255,137,137,0.15)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "15px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "15px", flex: 1 }}>
            <h5 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "#242424" }}>
              {isRoot ? "Root Group" : `Group ${path.join(".")}`}
            </h5>
            <Select
              value={node.op || "AND"}
              onChange={(e) => updateNode(path, { op: e.target.value })}
              className="form-control"
              style={{ width: "auto", minWidth: "120px", maxWidth: "150px" }}
            >
              <option value="AND">AND</option>
              <option value="OR">OR</option>
            </Select>
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => addCondition(path)}
              style={{ 
                fontSize: "13px", 
                padding: "6px 12px",
                backgroundColor: "#FF8989",
                borderColor: "#FF8989",
                color: "#fff"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#ff6b6b";
                e.target.style.borderColor = "#ff6b6b";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#FF8989";
                e.target.style.borderColor = "#FF8989";
              }}
            >
              <AddIcon style={{ fontSize: "16px", marginRight: "4px", verticalAlign: "middle" }} />
              Condition
            </button>
            {/* Group button commented out - not needed currently */}
            {/* <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => addGroup(path)}
              style={{ 
                fontSize: "13px", 
                padding: "6px 12px",
                backgroundColor: "#FF8989",
                borderColor: "#FF8989",
                color: "#fff"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#ff6b6b";
                e.target.style.borderColor = "#ff6b6b";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#FF8989";
                e.target.style.borderColor = "#FF8989";
              }}
            >
              <AddIcon style={{ fontSize: "16px", marginRight: "4px", verticalAlign: "middle" }} />
              Group
            </button> */}
            {!isRoot && (
              <IconButton
                size="small"
                onClick={() => removeNode(path)}
                style={{ color: "#d32f2f", padding: "6px" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        </div>
        <hr style={{ margin: "20px 0", borderColor: "#e1dfdf" }} />
        {node.children && node.children.length > 0 ? (
          <div style={{ marginLeft: isRoot ? "0" : "20px" }}>
            {node.children.map((child, index) => (
              <div
                key={index}
                style={{
                  marginBottom: child.type === "condition" ? "25px" : "30px",
                  position: "relative",
                }}
              >
                {child.type === "condition"
                  ? renderCondition(child, [...path, index])
                  : null /* Nested groups disabled - not needed currently */}
                {index < node.children.length - 1 && child.type === "condition" && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "15px",
                      marginBottom: "15px",
                    }}
                  >
                    <span
                      style={{
                        padding: "4px 12px",
                        backgroundColor: node.op === "OR" ? "#FF8989" : "#FF8989",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#fff",
                        textTransform: "uppercase",
                      }}
                    >
                      {node.op || "AND"}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "50px 20px",
              color: "#999",
              fontStyle: "italic",
              backgroundColor: "#fafafa",
              borderRadius: "8px",
              border: "2px dashed #ddd",
              marginTop: "10px",
            }}
          >
            <p style={{ margin: 0, fontSize: "14px", lineHeight: "1.6" }}>
              No conditions in this group.
              <br />
              Click <strong>"+ Condition"</strong> to get started.
            </p>
          </div>
        )}
      </div>
    );
  };
  // } // End of commented renderGroup function

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="WrapperArea">
      <div className="WrapperBox">
        <div className="TitleBox">
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <Link to={segment.SEGMENT_LIST}>
              <IconButton style={{ padding: "8px" }}>
                <BackIcon />
              </IconButton>
            </Link>
            <h4 className="Title">{id ? "Edit Segment" : "Create Segment"}</h4>
          </div>
          <div>
            <button
              type="button"
              className="btn btn-primary"
              ref={buttonref}
              onClick={handleSave}
              style={{ 
                backgroundColor: "#FF8989", 
                borderColor: "#FF8989", 
                color: "#fff"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#ff6b6b";
                e.target.style.borderColor = "#ff6b6b";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#FF8989";
                e.target.style.borderColor = "#FF8989";
              }}
            >
              <SaveIcon style={{ fontSize: "18px", marginRight: "5px", verticalAlign: "middle" }} />
              Save Segment
            </button>
          </div>
        </div>

        {/* Segment Details Section */}
        <div className="FilterBox FilterArea" style={{ 
          marginBottom: "30px",
          padding: "30px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
        }}>
         
          
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px"
          }}>
            {/* Segment Name - Full Width */}
            <div>
            <h5 style={{ 
              margin: 0, 
              fontSize: "20px", 
              fontWeight: "700", 
              color: "#242424" 
            }}>
              Segment Details
            </h5>
              <Input
                type="text"
                label="Segment Name *"
                value={segmentData.name}
                onChange={(e) => {
                  const sanitizedText = allowAlphaNumericWithSpace(e.target.value);
                  setSegmentData({ ...segmentData, name: sanitizedText });
                }}
                className="form-control"
                required
                error={!segmentData.name && error.name}
                maxLength={50}
              />
              {!segmentData.name && error.name && (
                <small style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  {error.name}
                </small>
              )}
            </div>
            
            {/* Interakt Tag and Status - Side by Side */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px"
            }}>
              <div>
                <Input
                  type="text"
                  label="Interakt Tag *"
                  value={segmentData.interakt_tag}
                  onChange={(e) => setSegmentData({ ...segmentData, interakt_tag: e.target.value })}
                  className="form-control"
                  required
                  error={!segmentData.interakt_tag && error.interakt_tag}
                  placeholder="e.g., vip_customers, active_users"
                />
                {!segmentData.interakt_tag && error.interakt_tag && (
                  <small style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", display: "block" }}>
                    {error.interakt_tag}
                  </small>
                )}
                <small style={{ color: "#666", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  Unique tag name for Interakt sync (lowercase, underscores only)
                </small>
              </div>
              
              <div>
                <Select
                  label="Status"
                  value={segmentData.status}
                  onChange={(e) => setSegmentData({ ...segmentData, status: e.target.value })}
                  className="form-control"
                >
                  <option value="draft">Draft</option>
                  <option value="ready">Ready</option>
                  <option value="applied">Applied</option>
                  <option value="paused">Paused</option>
                </Select>
                <small style={{ color: "#666", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  Draft: Not ready | Ready: Ready to apply | Applied: Active | Paused: Temporarily disabled
                </small>
              </div>
            </div>
            
            {/* Description - Full Width */}
            <div>
              <TextArea
                label="Description (Optional)"
                value={segmentData.description}
                onChange={(e) => setSegmentData({ ...segmentData, description: e.target.value })}
                className="form-control"
                rows={3}
                placeholder="Describe what this segment represents..."
              />
            </div>
          </div>
        </div>

        {/* Segment Logic Section - Side by Side Layout */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 2fr",
          gap: "24px"
        }}>
          {/* Left Panel - Segment Logic Controls */}
          <div className="FilterBox FilterArea" style={{
            padding: "30px",
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
          }}>
           

            {/* Match Logic Selector */}
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px"
            }}>
              <h5 style={{ 
                margin: 0, 
                fontSize: "20px", 
                fontWeight: "700", 
                color: "#242424" 
              }}>
                Segment Logic
              </h5>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                
                <label style={{ 
                  margin: 0, 
                  fontSize: "15px", 
                  fontWeight: "600", 
                  color: "#333",
                  whiteSpace: "nowrap"
                }}>
                  Match Conditions:
                </label>
                <Select
                  value={segmentData.logic.op || "AND"}
                  onChange={(e) => {
                    setSegmentData({
                      ...segmentData,
                      logic: {
                        ...segmentData.logic,
                        op: e.target.value,
                      },
                    });
                  }}
                  className="form-control"
                  style={{
                    minWidth: "140px",
                    fontSize: "14px",
                    padding: "8px 12px",
                    backgroundColor: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  <option value="AND">ALL (AND)</option>
                  <option value="OR">ANY (OR)</option>
                </Select>
              </div>
              <div style={{
                padding: "10px 16px",
                backgroundColor: segmentData.logic.op === "AND" ? "#e3f2fd" : "#fff3e0",
                borderRadius: "6px",
                border: `1px solid ${segmentData.logic.op === "AND" ? "#90caf9" : "#ffcc80"}`,
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                <span style={{ fontSize: "16px" }}>✓</span>
                <span style={{ 
                  fontSize: "13px", 
                  color: segmentData.logic.op === "AND" ? "#1976d2" : "#f57c00",
                  fontWeight: "500"
                }}>
                  {segmentData.logic.op === "AND"
                    ? "All conditions must be true for a user to match"
                    : "At least one condition must be true for a user to match"}
                </span>
              </div>
              <button
                type="button"
                onClick={() => addCondition([])}
                style={{ 
                  fontSize: "14px", 
                  padding: "10px 20px",
                  backgroundColor: "#FF8989",
                  border: "none",
                  borderRadius: "6px",
                  color: "#fff",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 4px rgba(255, 137, 137, 0.3)",
                  width: "100%"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#ff6b6b";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = "0 4px 8px rgba(255, 137, 137, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#FF8989";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 2px 4px rgba(255, 137, 137, 0.3)";
                }}
              >
                <AddIcon style={{ fontSize: "18px" }} />
                Add Condition
              </button>
            </div>

            {error.logic && (
              <div style={{
                marginTop: "20px",
                padding: "14px 18px",
                backgroundColor: "#ffebee",
                border: "1px solid #ffcdd2",
                borderRadius: "6px",
                color: "#c62828",
                fontSize: "14px",
                fontWeight: "500"
              }}>
                ⚠ {error.logic}
              </div>
            )}
          </div>

          {/* Right Panel - Conditions */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px"
          }}>
            {segmentData.logic.children && segmentData.logic.children.length > 0 ? (
              segmentData.logic.children
                .filter((child) => child.type === "condition")
                .map((child, index) => (
                  <div key={index}>
                    {renderCondition(child, [index])}
                  </div>
                ))
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 30px",
                  color: "#666",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "10px",
                  border: "2px dashed #ddd",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "300px"
                }}
              >
                <div style={{
                  fontSize: "48px",
                  marginBottom: "16px",
                  opacity: 0.5
                }}>
                  📋
                </div>
                <p style={{ 
                  margin: 0, 
                  fontSize: "16px", 
                  lineHeight: "1.6",
                  fontWeight: "500",
                  color: "#333",
                  marginBottom: "8px"
                }}>
                  No conditions added yet
                </p>
                <p style={{ 
                  margin: 0, 
                  fontSize: "14px", 
                  lineHeight: "1.6",
                  color: "#666"
                }}>
                  Click <strong style={{ color: "#FF8989" }}>"Add Condition"</strong> to get started
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegmentBuilder;
